import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, TextField, MenuItem, Divider,
  Table, TableBody, TableHead, TableRow, TableCell, CircularProgress, Alert,
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';

import { COLORS } from '../theme/constants';

const { primary: LAB_PRIMARY, teal: LAB_TEAL } = COLORS;

const PAYMENT_METHODS = [
  { value: 'tarjeta_debito',    label: 'Tarjeta Débito' },
  { value: 'tarjeta_credito',   label: 'Tarjeta Crédito' },
  { value: 'webpay',            label: 'Webpay' },
  { value: 'transferencia',     label: 'Transferencia Bancaria' },
  { value: 'deposito',          label: 'Depósito' },
];

const fmt = (n) => Number(n || 0).toLocaleString('es-CL');

export default function PaymentDialog({ open, onClose, onPay, exams = [], orderNumber = '' }) {
  const [paymentMethod, setPaymentMethod] = useState('');
  const [boletaNumber, setBoletaNumber]   = useState('');
  const [voucherNumber, setVoucherNumber] = useState('');
  const [last4Digits, setLast4Digits]     = useState('');
  const [paymentNotes, setPaymentNotes]   = useState('');
  const [paying, setPaying]               = useState(false);
  const [error, setError]                 = useState('');

  const total    = exams.reduce((sum, e) => sum + (Number(e.precioArancel) || Math.round((Number(e.precioNeto) || 0) * 1.19)), 0);
  const subtotal = Math.round(total / 1.19);
  const iva      = total - subtotal;

  const handleReset = () => {
    setPaymentMethod('');
    setBoletaNumber('');
    setVoucherNumber('');
    setLast4Digits('');
    setPaymentNotes('');
    setError('');
    setPaying(false);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handlePay = async () => {
    if (!paymentMethod) { setError('Selecciona un método de pago.'); return; }
    setPaying(true);
    setError('');
    try {
      await onPay({ paymentMethod, boletaNumber, voucherNumber, last4Digits, paymentNotes, subtotal, iva, total });
      handleReset();
    } catch (err) {
      setError(err.message || 'Error al registrar el pago.');
      setPaying(false);
    }
  };

  const needsVoucherAndLast4 = ['tarjeta_debito', 'tarjeta_credito', 'webpay'].includes(paymentMethod);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: LAB_PRIMARY, color: '#fff', display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
        <AttachMoneyIcon />
        Registrar Pago — Orden {orderNumber}
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="subtitle2" fontWeight={700} color={LAB_PRIMARY} mb={1}>
          Resumen de Exámenes
        </Typography>

        <Table size="small" sx={{ mb: 2, border: '1px solid #E2E8F0', borderRadius: 2 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#F4F6F8' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: LAB_PRIMARY }}>Examen</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: LAB_PRIMARY }}>Precio (c/IVA)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {exams.map((e) => {
              const precio = Number(e.precioArancel) || Math.round((Number(e.precioNeto) || 0) * 1.19);
              return (
                <TableRow key={e.examTypeId}>
                  <TableCell sx={{ fontSize: '0.82rem' }}>{e.examTypeName}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                    {precio ? `$${fmt(precio)}` : <span style={{ color: '#9CA3AF' }}>Sin precio</span>}
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow sx={{ bgcolor: '#F0FDF4' }}>
              <TableCell sx={{ fontWeight: 700, color: LAB_PRIMARY, fontSize: '0.88rem' }}>TOTAL (IVA incluido)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: LAB_TEAL, fontSize: '1rem' }}>
                ${fmt(total)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" fontWeight={700} color={LAB_PRIMARY} mb={1.5}>
          Datos del Pago
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            select fullWidth size="small" label="Método de Pago *"
            value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
            {PAYMENT_METHODS.map((m) => (
              <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth size="small" label="N° Boleta / Comprobante"
            value={boletaNumber} onChange={(e) => setBoletaNumber(e.target.value)}
            InputProps={{
              startAdornment: <ReceiptIcon sx={{ fontSize: 16, color: 'text.disabled', mr: 0.5 }} />,
            }}
          />

          {needsVoucherAndLast4 && (
            <>
              <TextField
                fullWidth size="small" label="N° Voucher"
                value={voucherNumber} onChange={(e) => setVoucherNumber(e.target.value)}
              />
              <TextField
                fullWidth size="small" label="Últimos 4 dígitos de la tarjeta"
                value={last4Digits} onChange={(e) => setLast4Digits(e.target.value)}
                inputProps={{ maxLength: 4, pattern: '[0-9]*' }}
                placeholder="****"
              />
            </>
          )}

          <TextField
            fullWidth size="small" label="Observaciones del pago"
            multiline rows={2}
            value={paymentNotes} onChange={(e) => setPaymentNotes(e.target.value)}
          />
        </Box>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={handleClose} disabled={paying} sx={{ color: 'text.secondary' }}>
          Omitir por ahora
        </Button>
        <Button
          variant="contained"
          onClick={handlePay}
          disabled={paying}
          startIcon={paying ? <CircularProgress size={16} color="inherit" /> : <AttachMoneyIcon />}
          sx={{ bgcolor: LAB_TEAL, fontWeight: 700, px: 3, '&:hover': { bgcolor: '#229e87' } }}>
          {paying ? 'Registrando...' : `Pagar $${fmt(total)}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
